import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {
  OfferSetValidationRuleModel,
  OfferSetValidationRulePayload
} from '../model/offer-set-validation-rule.model';
import {AbstractHttpService} from './abstract-http-service';

@Injectable({
  providedIn: 'root'
})
export class OfferSetValidationService extends AbstractHttpService {
  private readonly endpointUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offer-set-validation-rules`;
  private readonly readOptions = {
    headers: this.acceptJsonHeaders,
    withCredentials: true
  };
  private readonly createOptions = {
    headers: this.contentTypeJsonHeaders,
    withCredentials: true
  };
  private readonly updateOptions = {
    headers: this.contentTypeJsonHeadersForPatch,
    withCredentials: true
  };
  private readonly deleteOptions = {
    headers: this.acceptJsonHeaders,
    withCredentials: true
  };

  /** Creates the service with the HTTP client used for rule requests. */
  constructor(private httpClient: HttpClient) {
    super();
  }

  /** Loads all offer set validation rules for the current administrator. */
  getRules(): Observable<OfferSetValidationRuleModel[]> {
    const rules = this.httpClient.get<OfferSetValidationRuleModel[]>(
      this.endpointUrl,
      this.readOptions
    ).pipe(
      catchError(this.handleError)
    );

    return rules;
  }

  /** Creates a rule using only its writable properties. */
  createRule(
    payload: OfferSetValidationRulePayload
  ): Observable<OfferSetValidationRuleModel> {
    const rule = this.httpClient.post<OfferSetValidationRuleModel>(
      this.endpointUrl,
      payload,
      this.createOptions
    ).pipe(
      catchError(this.handleError)
    );

    return rule;
  }

  /** Updates an existing rule using the merge-patch API contract. */
  updateRule(
    id: number,
    payload: OfferSetValidationRulePayload
  ): Observable<OfferSetValidationRuleModel> {
    const rule = this.httpClient.patch<OfferSetValidationRuleModel>(
      `${this.endpointUrl}/${id}`,
      payload,
      this.updateOptions
    ).pipe(
      catchError(this.handleError)
    );

    return rule;
  }

  /** Deletes a persisted offer set validation rule by its identifier. */
  deleteRule(id: number): Observable<void> {
    const deletion = this.httpClient.delete<void>(
      `${this.endpointUrl}/${id}`,
      this.deleteOptions
    ).pipe(
      catchError(this.handleError)
    );

    return deletion;
  }
}
